import * as React from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  IonContent,
  IonPage,
  useIonViewWillLeave,
  useIonViewWillEnter,
} from "@ionic/react";

import useWaitApiCall from "../../../hooks/useWaitApiCall";
import {
  Context,
  createReport,
  clearRealizations,
  fetchCategories,
  clearSuccessMessage,
  clearSuccessResult,
  fetchTeachers,
  fetchGearBoxes,
  CREATE_REALIZATION_BY_CONVERSION_SUCCESS_MESSAGE,
  CREATE_REALIZATION_SUCCESS_MESSAGE,
  CREATE_CATEGORY_SUCCESS_MESSAGE,
  CREATE_COMMENT_SUCCESS_MESSAGE,
  UPDATE_COMMENT_SUCCESS_MESSAGE,
  DELETE_COMMENT_SUCCESS_MESSAGE,
  CREATE_REALIZATION_STAMP_REACTION_SUCCESS,
  categorizedRealizationPath,
  fetchClasses,
  fetchGearthemeRealization,
  createComment,
  updateComment,
  deleteComment,
  fetchComments,
  clearComments,
  createRealizationStampReaction,
  addFavorite,
  removeFavorite,
} from "../../../store/student";
import styles from "../common.module.scss";
import ThemeHeader from "../../../molecules/ThemeHeader";
import FiltableHeader from "../../../organisms/NewFiltableHeader";
import TimelineTheme from "../../../templates/TimelineTheme";
import { Stamp } from "../../../libs/stamp";
import { calcUnreadNotiCount } from "../../../libs/calcUnreadNotiCount";

const TimelineThemePage = () => {
  const { dispatch, contextState } = React.useContext(Context);
  const [calling, peep] = useWaitApiCall(contextState);
  const params = useParams<{ id: string }>();
  const history = useHistory();
  const [searchText, setSearchText] = React.useState("");
  const [isDisplaying, setIsDisplaying] = React.useState(false);

  const fetchRealizationGearthemeForSearch = (searchText?: string) => {
    setSearchText(searchText || "");
    dispatch(
      peep(fetchGearthemeRealization(Number(params.id), null, searchText)),
    );
  };

  const loadRealizations = React.useCallback(() => {
    dispatch(peep(clearSuccessMessage()));
    const list = contextState.realizations;
    dispatch(
      peep(
        fetchGearthemeRealization(
          Number(params.id),
          list[list.length - 1].id,
          searchText,
        ),
      ),
    );
  }, [dispatch, peep, contextState.realizations, params.id, searchText]);

  useIonViewWillEnter(() => {
    dispatch(fetchCategories());
    fetchRealizationGearthemeForSearch();
    setIsDisplaying(true);
    dispatch(fetchTeachers());
    dispatch(fetchGearBoxes({ is_check_archived: true }));
    dispatch(fetchClasses());
  }, [params.id]);

  useIonViewWillLeave(() => {
    dispatch(clearRealizations());
    setIsDisplaying(false);
  });

  React.useEffect(() => {
    if (calling) return;
    const categoryIDs = contextState.realization?.categories?.map(
      category => category.id,
    );
    if (contextState.success_message === CREATE_CATEGORY_SUCCESS_MESSAGE) {
      dispatch(clearSuccessMessage());
      dispatch(peep(fetchCategories()));
    } else if (
      contextState.success_message === CREATE_REALIZATION_SUCCESS_MESSAGE ||
      contextState.success_message ===
        CREATE_REALIZATION_BY_CONVERSION_SUCCESS_MESSAGE
    ) {
      dispatch(clearSuccessMessage());
      history.push(`${categorizedRealizationPath}/${categoryIDs?.[0] ?? 0}`);
    }
  }, [
    dispatch,
    contextState.success_message,
    contextState.realization,
    history,
    calling,
    peep,
  ]);

  React.useEffect(() => {
    if (
      contextState.success_message === CREATE_COMMENT_SUCCESS_MESSAGE ||
      contextState.success_message === UPDATE_COMMENT_SUCCESS_MESSAGE ||
      contextState.success_message === DELETE_COMMENT_SUCCESS_MESSAGE ||
      contextState.success_message === CREATE_REALIZATION_STAMP_REACTION_SUCCESS
    ) {
      dispatch(clearSuccessMessage());
      dispatch(peep(fetchComments(contextState.success_result["id"])));
      dispatch(clearSuccessResult());
    }
  }, [
    dispatch,
    contextState.success_message,
    contextState.success_result,
    peep,
  ]);

  const createReportFunc = React.useCallback(
    (params: { realization_id?: number; comment_id?: number }) => {
      if (calling) return;
      dispatch(peep(createReport(params)));
    },
    [calling, dispatch, peep],
  );

  const createCommentFunc = React.useCallback(
    (realization_id: number, content: string) => {
      dispatch(peep(createComment(realization_id, content)));
    },
    [dispatch, peep],
  );

  const updateCommentFunc = React.useCallback(
    (comment_id: number, content: string) => {
      if (calling) return;
      dispatch(peep(updateComment(comment_id, content)));
    },
    [calling, dispatch, peep],
  );

  const deleteCommentFunc = React.useCallback(
    (comment_id: number) => {
      if (calling) return;
      dispatch(peep(deleteComment(comment_id)));
    },
    [calling, dispatch, peep],
  );

  const fetchCommentsFunc = React.useCallback(
    (realization_id: number) => {
      dispatch(fetchComments(realization_id));
    },
    [dispatch],
  );

  const clearOldCommentsFunc = React.useCallback(() => {
    dispatch(clearComments());
  }, [dispatch]);

  const createStampReactionFunc = React.useCallback(
    (realization_id: number, stamp: Stamp) => {
      dispatch(
        peep(
          createRealizationStampReaction(realization_id, {
            type: "comment",
            realization_stamp_id: stamp.id,
          }),
        ),
      );
    },
    [dispatch, peep],
  );

  const addFavoriteFunc = React.useCallback(
    (id: number) => {
      if (calling) return;
      dispatch(peep(addFavorite(id)));
    },
    [calling, dispatch, peep],
  );

  const removeFavoriteFunc = React.useCallback(
    (id: number) => {
      if (calling) return;
      dispatch(peep(removeFavorite(id)));
    },
    [calling, dispatch, peep],
  );

  return (
    <IonPage>
      <FiltableHeader
        title="みんなの回答"
        unreadNotiCount={calcUnreadNotiCount(contextState)}
        searchText={searchText}
        goBack
        onChangeFunc={fetchRealizationGearthemeForSearch}
      />
      <IonContent className={`${styles.wrapper} ${styles.bgWhileBase}`}>
        <div className={styles.container}>
          {contextState.geartheme && (
            <ThemeHeader
              geartheme={contextState.geartheme}
              isTimelinePage={true}
            />
          )}
          <TimelineTheme
            isDisplaying={isDisplaying}
            calling={calling}
            current_id={contextState.student.id}
            realizations={contextState.realizations}
            comments={contextState.comments}
            searchText={searchText}
            student_id={contextState.student.id}
            geartheme_pagination={contextState.geartheme_pagination}
            loadRealizations={loadRealizations}
            createReport={createReportFunc}
            createComment={createCommentFunc}
            updateComment={updateCommentFunc}
            deleteComment={deleteCommentFunc}
            fetchComments={fetchCommentsFunc}
            clearOldComments={clearOldCommentsFunc}
            createStampReaction={(realization_id, _type, stamp) =>
              createStampReactionFunc(realization_id, stamp)
            }
            addFavorite={addFavoriteFunc}
            removeFavorite={removeFavoriteFunc}
          />
        </div>
      </IonContent>
    </IonPage>
  );
};

export default TimelineThemePage;
